import { Component, HostListener, ChangeDetectionStrategy, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../core/auth/auth.service';
import { AuthUser } from '../../core/auth/auth.models';
import { NavigationService } from '../../core/services/navigation.service';
import { ThemeService } from '../../core/services/theme.service';
import { ToastService } from '../../core/services/toast.service';
import { DmpIconName } from '../../shared/icons/icon-paths.constants';
import { APP } from '../../core/constants/app.constants';
import { BrandingService } from '../../core/branding/branding.service';
import { GetterSetterService } from '../../core/services/getter-setter.service';

@Component({
    selector: 'dmp-topbar',
    templateUrl: './topbar.component.html',
    styleUrls: ['./topbar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TopbarComponent implements OnInit, OnDestroy {
  private readonly branding = inject(BrandingService);
  get appName(): string {
    return this.branding.appName();
  }
  userMenuOpen = false;
  userDetails: Record<string, unknown> = {};
  private readonly destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private navService: NavigationService,
    private themeService: ThemeService,
    private toastService: ToastService,
    private authService: AuthService,
    private getSetService: GetterSetterService,
  ) {}
  ngOnInit(): void {
    this.refreshUserDetails();
    this.authService.currentUser$.pipe(takeUntil(this.destroy$)).subscribe(() => this.refreshUserDetails());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get user(): AuthUser | null {
    return this.authService.currentUser;
  }

  get tenantId(): string {
    return String(this.userDetails['tenantId'] ?? '').trim();
  }

  get tenantName(): string {
    return String(this.userDetails['tenantName'] ?? '').trim();
  }

  get tenantBadgeLabel(): string {
    return this.tenantName || this.tenantId;
  }

  get hasTenantBadge(): boolean {
    return this.tenantId.length > 0 && !this.userDetails['platformAdmin'];
  }

  get tenantBadgeTitle(): string {
    return this.tenantName
      ? `${this.tenantName} (${this.tenantId})`
      : this.tenantId;
  }

  get tenantBadgeAriaLabel(): string {
    return this.tenantName
      ? `Tenant ${this.tenantName}, id ${this.tenantId}`
      : `Tenant id ${this.tenantId}`;
  }

  get displayFirstName(): string {
    return this.readUserField('firstName');
  }

  get displayLastName(): string {
    return this.readUserField('lastName');
  }

  get displayEmail(): string {
    return this.readUserField('email');
  }

  get displayRole(): string {
    const role = this.readUserField('role');
    if (role) return role;
    const roleNames = this.userDetails['roleNames'];
    if (Array.isArray(roleNames) && roleNames.length) {
      return roleNames.filter((n): n is string => typeof n === 'string' && !!n.trim()).join(', ');
    }
    return APP.USER.ROLE;
  }

  get displayInitials(): string {
    return this.readUserField('initials') || APP.USER.INITIALS;
  }

  get displayName(): string {
    const u = this.user;
    return u ? `@${u.firstName}` : APP.USER.NAME;
  }

  @HostListener('document:click')
  closeUserMenu(): void {
    this.userMenuOpen = false;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.userMenuOpen = false;
  }

  toggleUserMenu(event: Event): void {
    event.stopPropagation();
    this.userMenuOpen = !this.userMenuOpen;
  }

  onProfile(event: Event): void {
    event.stopPropagation();
    this.userMenuOpen = false;
    this.toastService.show('Profile — coming soon');
  }

  onSettings(event: Event): void {
    event.stopPropagation();
    this.userMenuOpen = false;
    this.router.navigate(['/auth/change-password']);
  }

  onLogout(event: Event): void {
    event.stopPropagation();
    this.userMenuOpen = false;
    this.authService.logout();
    this.toastService.show('Logged out successfully');
    this.router.navigate(['/auth/sign-in']);
  }

  get themeToggleIcon(): DmpIconName {
    return this.themeService.theme === 'light' ? 'moon' : 'sun';
  }

  get themeToggleLabel(): string {
    return this.themeService.theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode';
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  get isOverview(): boolean {
    return this.router.url === '/';
  }

  get isAssistant(): boolean {
    return this.router.url === '/assistant';
  }

  get showTopTabs(): boolean {
    return this.isOverview || this.isAssistant;
  }

  get sidebarCollapsed(): boolean {
    return this.navService.isCollapsed;
  }

  get isMobile(): boolean {
    return this.navService.isMobile;
  }

  get collapseToggleTitle(): string {
    return this.sidebarCollapsed ? 'Expand menu' : 'Collapse menu';
  }

  navigateTo(path: string): void {
    this.router.navigate([path]);
  }

  toggleMobileSidebar(): void {
    this.navService.toggleSidebar();
  }

  toggleSidebarCollapse(): void {
    this.navService.toggleCollapse();
  }

  private refreshUserDetails(): void {
    const details = this.getSetService.getUserDetails();
    this.userDetails =
      details && typeof details === 'object' && !Array.isArray(details)
        ? (details as Record<string, unknown>)
        : {};
  }

  private readUserField(key: string): string {
    const value = this.userDetails[key];
    return typeof value === 'string' && value.trim() ? value.trim() : '';
  }
}
